export interface SvgColorInfo {
  value: string;
  hex: string;
  count: number;
  attributes: ('fill' | 'stroke')[];
  label: string;
}

const IGNORED = new Set(['none', 'transparent', 'inherit', 'currentcolor', '']);

const NAMED_COLORS: Record<string, string> = {
  black: '#000000', white: '#ffffff', red: '#ff0000',
  green: '#008000', blue: '#0000ff', yellow: '#ffff00',
  cyan: '#00ffff', magenta: '#ff00ff', orange: '#ffa500',
  purple: '#800080', pink: '#ffc0cb', gray: '#808080',
  grey: '#808080', silver: '#c0c0c0', navy: '#000080',
  teal: '#008080', maroon: '#800000', olive: '#808000',
  lime: '#00ff00', aqua: '#00ffff', fuchsia: '#ff00ff',
  coral: '#ff7f50', salmon: '#fa8072', tomato: '#ff6347',
  gold: '#ffd700', khaki: '#f0e68c', tan: '#d2b48c',
  peru: '#cd853f', sienna: '#a0522d', chocolate: '#d2691e',
  firebrick: '#b22222', indianred: '#cd5c5c', darkred: '#8b0000',
  darkgreen: '#006400', darkblue: '#00008b', darkcyan: '#008b8b',
  darkorange: '#ff8c00', deeppink: '#ff1493', deepskyblue: '#00bfff',
  dodgerblue: '#1e90ff', forestgreen: '#228b22', goldenrod: '#daa520',
  hotpink: '#ff69b4', lightblue: '#add8e6', lightcoral: '#f08080',
  lightcyan: '#e0ffff', lightgray: '#d3d3d3', lightgreen: '#90ee90',
  lightpink: '#ffb6c1', lightsalmon: '#ffa07a', lightskyblue: '#87cefa',
  limegreen: '#32cd32', midnightblue: '#191970', royalblue: '#4169e1',
  saddlebrown: '#8b4513', seagreen: '#2e8b57', skyblue: '#87ceeb',
  slategray: '#708090', springgreen: '#00ff7f', steelblue: '#4682b4',
  turquoise: '#40e0d0', violet: '#ee82ee', wheat: '#f5deb3',
  yellowgreen: '#9acd32', whitesmoke: '#f5f5f5', snow: '#fffafa',
  ivory: '#fffff0', mintcream: '#f5fffa', lavender: '#e6e6fa',
  linen: '#faf0e6', oldlace: '#fdf5e6', papayawhip: '#ffefd5',
  gainsboro: '#dcdcdc', beige: '#f5f5dc', cornsilk: '#fff8dc',
};

const REVERSE_NAMES: Record<string, string> = {
  '#000000': 'Black', '#ffffff': 'White', '#ff0000': 'Red',
  '#008000': 'Green', '#0000ff': 'Blue', '#ffff00': 'Yellow',
  '#00ffff': 'Cyan', '#ff00ff': 'Magenta', '#ffa500': 'Orange',
  '#808080': 'Gray', '#800080': 'Purple', '#008080': 'Teal',
  '#ffd700': 'Gold', '#ff6347': 'Tomato', '#fa8072': 'Salmon',
};

function normalizeToHex(color: string): string | null {
  const trimmed = color.trim().toLowerCase();
  if (trimmed.startsWith('#')) {
    if (trimmed.length === 4) {
      return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`;
    }
    if (trimmed.length === 7) return trimmed;
    return null;
  }

  const rgbMatch = trimmed.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
    const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
    const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }

  return NAMED_COLORS[trimmed] || null;
}

function getColorLabel(hex: string): string {
  return REVERSE_NAMES[hex.toLowerCase()] || hex.toUpperCase();
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getColorVariants(hex: string): string[] {
  const variants = [hex, hex.toUpperCase()];
  const h = hex.replace('#', '');

  if (h[0] === h[1] && h[2] === h[3] && h[4] === h[5]) {
    const short = `#${h[0]}${h[2]}${h[4]}`;
    variants.push(short, short.toUpperCase());
  }

  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  variants.push(`rgb(${r},${g},${b})`, `rgb(${r}, ${g}, ${b})`);

  const lower = hex.toLowerCase();
  for (const [name, nameHex] of Object.entries(NAMED_COLORS)) {
    if (nameHex === lower) {
      variants.push(name);
      variants.push(name.charAt(0).toUpperCase() + name.slice(1));
      break;
    }
  }

  return variants;
}

export function extractSvgColors(svgMarkup: string): SvgColorInfo[] {
  const colorMap = new Map<string, { value: string; count: number; attributes: Set<'fill' | 'stroke'> }>();

  const processMatches = (regex: RegExp, attr: 'fill' | 'stroke') => {
    let match;
    while ((match = regex.exec(svgMarkup)) !== null) {
      const rawColor = match[1].trim();
      if (IGNORED.has(rawColor.toLowerCase())) continue;
      const hex = normalizeToHex(rawColor);
      if (!hex) continue;

      const entry = colorMap.get(hex);
      if (entry) {
        entry.count++;
        entry.attributes.add(attr);
      } else {
        colorMap.set(hex, { value: rawColor, count: 1, attributes: new Set([attr]) });
      }
    }
  };

  processMatches(/fill="([^"]+)"/g, 'fill');
  processMatches(/stroke="([^"]+)"/g, 'stroke');
  processMatches(/style="[^"]*fill:\s*([^;"]+)/g, 'fill');
  processMatches(/style="[^"]*stroke:\s*([^;"]+)/g, 'stroke');

  return Array.from(colorMap.entries()).map(([hex, info]) => ({
    value: info.value,
    hex,
    count: info.count,
    attributes: Array.from(info.attributes),
    label: getColorLabel(hex),
  }));
}

export function getSvgPrimaryFill(svgEl: { svgMarkup: string; fill?: string }): string | null {
  const duckFill = (svgEl as unknown as Record<string, unknown>).fill;
  if (typeof duckFill === 'string' && duckFill.length > 0) return duckFill;
  const extracted = extractSvgColors(svgEl.svgMarkup);
  return extracted.length > 0 ? extracted[0].hex : null;
}

export function replaceSvgColor(svgMarkup: string, oldHex: string, newHex: string): string {
  let result = svgMarkup;
  const variants = getColorVariants(oldHex);

  for (const variant of variants) {
    const escaped = escapeRegex(variant);
    result = result.replace(new RegExp(`(fill=")${escaped}(")`, 'gi'), `$1${newHex}$2`);
    result = result.replace(new RegExp(`(stroke=")${escaped}(")`, 'gi'), `$1${newHex}$2`);
    result = result.replace(new RegExp(`(fill:\\s*)${escaped}`, 'gi'), `$1${newHex}`);
    result = result.replace(new RegExp(`(stroke:\\s*)${escaped}`, 'gi'), `$1${newHex}`);
  }

  return result;
}

export function replaceSvgColors(svgMarkup: string, colorMap: Record<string, string>): string {
  let result = svgMarkup;
  for (const [oldHex, newHex] of Object.entries(colorMap)) {
    result = replaceSvgColor(result, oldHex, newHex);
  }
  return result;
}
